import React from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { Link } from 'react-router-dom';

import { formatHours as fm, formatNumber as fn, formatPercent as fp } from 'utils/utils';
import { ServiceTypes, Urls } from 'constants/constants';
import { formatDate } from 'utils/time';

import { twitchUrl } from 'store/models/Channel';
import Stream, { isStreamLive } from 'store/models/Stream';
import { isSessionRunning } from 'store/models/Session';
import { pathTo } from 'store/RouteState';

import Avatar from 'components/Avatar';
import { Menu, MenuItem } from 'components/Menu';
import ChannelCard, { ChannelCardLoading } from 'components/ChannelCard';
import * as Icons from 'components/Svg';
import { Button, MoreIconButton, TextButton, SlideshowIconButton, IconButtonList, SettingsIconButton } from 'components/Button';
import { StreamStatus, OverlayStatus, ChatStatus, TwitchStatus, YouTubeStatus } from 'components/ChannelStatus';
import { StatItem, StatItemList, StatItemMoney, StatItemNumber, StatItemPercent } from 'components/Stats';

import Dropdown from 'components/Dropdown';


// ======================
// PARTIALS
// ====================== 

export const ChannelList = ({ channels, channelSettings, streams, sessions, channelStats, loading, ...props }) => {
  return (
    <div className="g-channel-list">
      { loading && _.map(_.fill(Array(4), '0'), (_, idx) => <ChannelItemLoading key={idx} />)}

      { !loading &&
        _.map(channels, channel => (
          <ChannelItem 
            key={channel._id} 
            channel={channel} 
            settings={channelSettings?.[channel._id]}
            stream={streams?.[channel?._id]}
            session={sessions?.[channel?._id]}
            stats={channelStats?.[channel?._id]}
            {...props} />
      ))}
    </div>);
}

export const ChannelItemLoading = () => (
  <div className="g-channel-item loading">
    <div className="wrap-left">
      <div className="channel-info">
        <ChannelCardLoading />
        <div className="channel-status loading-text" />
      </div>
      <StatItemList>
        <StatItem loading={true} label="0" />
        <StatItem loading={true} label="0" />
        <StatItem loading={true} label="0" />
        <StatItem loading={true} label="0" />
      </StatItemList>
    </div>
  </div>
)

export const ChannelItem = ({ 
  channel, stream, session, stats, settings,
  onDelete, onTwitchView, onSessionCheck, onExport, onGetAssets, onSlideshow, onViewDetails, ...props }) => {
  let isLive = isStreamLive(stream);
  let isSessionLive = isSessionRunning(session);
  
  let onGetAssetsClick = e => {
    e.preventDefault();
    onGetAssets?.(channel._id, channel)
  }

  let onSlideshowClick = e => {
    e.preventDefault();
    onSlideshow?.(channel._id, channel)
  }
  let onExportClick = e => {
    e.preventDefault();
    onExport?.(channel._id, channel)
  }
  let onSessionCheckClick = e => {
    e.preventDefault();
    onSessionCheck?.(channel._id, channel)
  }
  let onTwitchViewClick = e => {
    e.preventDefault();
    onTwitchView?.(channel._id, channel)
  }
  let onDeleteClick = e => {
    e.preventDefault();
    onDelete?.(channel._id, channel)
  }

  let serviceIcon = channel?.service === ServiceTypes.TWITCH
    ? <Icons.TwitchIcon className="service" />
    : <Icons.YouTubeIcon className="service" />

  return (
    <Link className="g-channel-item" to={pathTo(Urls.CHANNEL_DETAILS_ROUTE, { channelId: channel._id })}>
      <div className="wrap-left">
        <div className="channel-info">
          <ChannelCard channel={channel} />
          <div className="channel-status">
            { channel?.service === ServiceTypes.TWITCH
              ? isLive 
                ? <Icons.TwitchLogo className="service" onClick={onTwitchViewClick} /> 
                : <Icons.TwitchIcon className="service" onClick={onTwitchViewClick} /> 
              : isLive 
                ? <Icons.YouTubeLogo className="service" onClick={onTwitchViewClick} />
                : <Icons.YouTubeIcon className="service" onClick={onTwitchViewClick} />}
            <StreamStatus on={isLive} onClick={onTwitchViewClick} />
            <OverlayStatus on={isSessionLive} onClick={onSessionCheckClick} />
            <ChatStatus on={isLive && settings?.enableChatbot} />
          </div>
        </div>
        <StatItemList>
          <StatItemMoney label="Media Value" value={stats?.totalMediaValue}/>
          <StatItemNumber label="Impressions" value={stats?.sceneViews} />
          <StatItemPercent label="Fill Rate" value={stats?.fillRate} />
          { channel?.service !== ServiceTypes.YOUTUBE && 
          <StatItemNumber label="Clicks" value={stats?.clicks} />}
        </StatItemList>
      </div>
      <IconButtonList className="channel-actions">
        <SlideshowIconButton size="m" onClick={onSlideshowClick} />
        <SettingsIconButton size="m" onClick={onGetAssetsClick} />
        <Menu
          target={props => <MoreIconButton size="m" {...props} />}>
          <MenuItem onClick={onExportClick}><Icons.SaveIcon /> Export Data</MenuItem>
          <MenuItem onClick={onSessionCheckClick}><Icons.HealthIcon /> Health Status</MenuItem>
          <MenuItem onClick={onTwitchViewClick}><Icons.TwitchIcon /> Twitch Channel</MenuItem>
          <MenuItem className="delete" onClick={onDeleteClick}><Icons.DeleteIcon /> Delete Channel</MenuItem>
        </Menu>
      </IconButtonList>
    </Link>);
}









// ======================
// COMPONENT
// ====================== 

export const ChannelStatsItemLoading = () => (
  <div className="channels-list">
    <div className="channel loading">
      <div className="streamer">
        <div className="avatar loading-shine"></div>
        <div className="channel-info">
          <div className="username loading-text"></div>
        </div>
      </div>
      <div className="stats loading-text"></div>
      <div className="actions"></div>
    </div>
  </div>
)

export default class ChannelStatsItem extends React.Component {

  state = {
    showDropdown: false,
  }

  onUrlClick = () => this.props.onGetUrl?.(this.props.channel?._id)
  onDeleteClick = () => this.props.onDelete?.(this.props.channel?._id)
  onAddCustomScene = () => this.props.onAddScene?.(this.props.channel?._id)
  onSessionClick = () => this.props.onSessionCheck(this.props.channel?._id)
  onTwitchClick = () => {
    window.open(twitchUrl(this.props.channel));
  }
  toggleInfo = () => this.setState({ showDropdown: !this.state.showDropdown })

  render() {
    let { channel, stats, isLive, isRunning, loading, onDelete, } = this.props;
    let { showDropdown } = this.state;
    let avatarUrl = channel?.avatarUrl;
    
    return (
      <div className={cx("g-channel-stats-item", { offline: !isLive })}>
        <div className="left">
          <div className="streamer">
            <Dropdown 
              target={(props, ref) => <Avatar ref={ref} size="m" onClick={this.toggleInfo} src={avatarUrl} />}
              show={showDropdown}
              position={'bottom right'}
              onOverlayClick={this.toggleInfo}>
              <div className="channel-dropdown">
                <div className="label">Created:</div>
                <div>{ formatDate(channel?.created) }</div>
              </div>  
            </Dropdown>
            <div className="channel-info">
              <div className="username">{ channel?.displayName ?? channel?.serviceName }</div>
              <div 
                className={cx("status hs", { live: isRunning, offline: !isRunning })}
                onClick={this.onSessionClick}>
                <i className="icon-rss-alt" /></div>
              <div 
                className={cx("status twitch", { live: isLive, offline: !isLive })}
                onClick={this.onTwitchClick}>
                <i className="icon-rss-alt" /></div>
            </div>
          </div>
          <div className={cx("stats", { 'loading-shine': loading })}>
            
              <div className="media">
                <span className="label">Media Value</span>
                <span className="value">${fn(stats?.mediaValue ?? 0)}</span>
              </div>
              <div className="impressions">
                <span className="label">Impressions</span>
                <span className="value">{fn(stats?.impressions ?? 0)}</span>
              </div>
              <div className="fill-rate">
                <span className="label">Fill Rate</span>
                <span className="value">
                  <span>{fp(stats?.fillRate ?? 0)}</span>
                </span>
              </div>
              <div className="average">
                <span className="label">Avg Viewers</span>
                <span className="value">{fn(stats?.avgViewers ?? 0)}</span>
              </div>
              <div className="minutes">
                <span className="label">Streamed</span>
                <span className="value">{fm(stats?.minutes ?? 0)}</span>
              </div>
          </div>
        </div>
        <div className="actions">
          <TextButton icon="link-intact" onClick={this.onUrlClick}>URL</TextButton>
          <Menu 
            target={props => <MoreIconButton {...props} />}>
            <MenuItem onClick={this.onAddCustomScene}>Add Custom Slide</MenuItem>
            <MenuItem onClick={this.onSessionClick}><Icons.HealthIcon /> Health Status</MenuItem>
            <MenuItem onClick={this.onTwitchClick}>Twitch Channel</MenuItem>
            <MenuItem className="delete" onClick={this.onDeleteClick}>Delete</MenuItem>
          </Menu>
        </div>
      </div>)
  }
}